import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Star, MessageSquare, TrendingUp, Users, Clock, CheckCircle,
  AlertCircle, Send, BarChart3, ThumbsUp, ThumbsDown, Meh, Smile
} from "lucide-react";

const EMOJI_RATINGS = ["😞", "😐", "🙂", "😊", "😍"];

export default function SatisfactionSurvey({ invitationId, onComplete }) {
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (invitationId) {
      loadSurvey();
    }
  }, [invitationId]);

  const loadSurvey = async () => {
    setLoading(true);
    try {
      // Get the invitation details
      const pendingRes = await api.get(`/phase24/surveys/pending?user_id=${invitationId}`);
      const invitation = pendingRes.data.surveys?.find(s => s.id === invitationId);
      
      if (invitation) {
        // Get the template
        const templateRes = await api.get(`/phase24/surveys/templates/${invitation.template_id}`);
        setSurvey({
          invitation,
          template: templateRes.data
        });
      } else {
        // Just load a default template for demo
        const templateRes = await api.get("/phase24/surveys/templates/visit_satisfaction");
        setSurvey({
          invitation: { id: invitationId, template_id: "visit_satisfaction" },
          template: templateRes.data
        });
      }
    } catch (error) {
      console.error("Failed to load survey:", error);
      toast.error("Failed to load survey");
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const isQuestionAnswered = (questionId) => {
    return responses[questionId] !== undefined;
  };

  const canProceed = () => {
    const currentQ = survey?.template?.questions?.[currentQuestion];
    if (!currentQ) return false;
    if (currentQ.required) {
      return isQuestionAnswered(currentQ.id);
    }
    return true;
  };

  const submitSurvey = async () => {
    setSubmitting(true);
    try {
      const formattedResponses = Object.entries(responses).map(([questionId, value]) => ({
        question_id: questionId,
        value
      }));

      const res = await api.post(`/phase24/surveys/${invitationId}/submit`, {
        responses: formattedResponses
      });

      setResult(res.data);
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
      
      if (onComplete) {
        onComplete(res.data);
      }
    } catch (error) {
      console.error("Failed to submit survey:", error);
      toast.error(error.response?.data?.detail || "Failed to submit survey");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="survey-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (submitted && result) {
    return (
      <Card className="max-w-lg mx-auto" data-testid="survey-complete">
        <CardContent className="py-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-muted-foreground mb-6">{result.thank_you_message}</p>
          
          {result.scores?.overall && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-2">Your Overall Rating</p>
              <p className="text-3xl font-bold text-primary">{result.scores.overall.percentage}%</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!survey?.template) {
    return (
      <Card data-testid="survey-not-found">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Survey not found</p>
        </CardContent>
      </Card>
    );
  }

  const questions = survey.template.questions || [];
  const currentQ = questions[currentQuestion];
  const totalQuestions = questions.length;
  const progressPercent = ((currentQuestion + 1) / totalQuestions) * 100;

  return (
    <div className="max-w-2xl mx-auto" data-testid="satisfaction-survey">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{survey.template.name}</CardTitle>
              <CardDescription>{survey.template.description}</CardDescription>
            </div>
            <Badge variant="outline">
              {currentQuestion + 1} of {totalQuestions}
            </Badge>
          </div>
          <Progress value={progressPercent} className="h-2 mt-4" />
        </CardHeader>

        <CardContent className="space-y-8">
          {currentQ && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2">{currentQ.text}</h3>
                {currentQ.required && (
                  <Badge variant="outline" className="text-xs">Required</Badge>
                )}
              </div>

              {/* Rating Question */}
              {currentQ.type === "rating" && (
                <div className="flex justify-center gap-2">
                  {[...Array(currentQ.scale || 5)].map((_, i) => {
                    const value = i + 1;
                    const isSelected = responses[currentQ.id] === value;
                    return (
                      <button
                        key={value}
                        onClick={() => handleResponse(currentQ.id, value)}
                        className={`w-14 h-14 rounded-full border-2 font-bold text-lg transition-all ${
                          isSelected
                            ? "bg-primary text-white border-primary scale-110"
                            : "bg-white hover:bg-slate-50 border-slate-200"
                        }`}
                        data-testid={`rating-${value}`}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* NPS Question */}
              {currentQ.type === "nps" && (
                <div>
                  <div className="flex justify-center gap-1 flex-wrap">
                    {[...Array(11)].map((_, i) => {
                      const isSelected = responses[currentQ.id] === i;
                      const color = i <= 6 ? "border-red-300 hover:bg-red-50" :
                                   i <= 8 ? "border-yellow-300 hover:bg-yellow-50" :
                                   "border-green-300 hover:bg-green-50";
                      return (
                        <button
                          key={i}
                          onClick={() => handleResponse(currentQ.id, i)}
                          className={`w-10 h-10 rounded-lg border-2 font-medium transition-all ${
                            isSelected
                              ? "bg-primary text-white border-primary"
                              : `bg-white ${color}`
                          }`}
                          data-testid={`nps-${i}`}
                        >
                          {i}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-3 text-sm text-muted-foreground">
                    <span>Not at all likely</span>
                    <span>Extremely likely</span>
                  </div>
                </div>
              )}

              {/* Emoji Question */}
              {currentQ.type === "emoji" && (
                <div className="flex justify-center gap-4">
                  {(currentQ.options || EMOJI_RATINGS).map((emoji, i) => {
                    const isSelected = responses[currentQ.id] === i + 1;
                    return (
                      <button
                        key={i}
                        onClick={() => handleResponse(currentQ.id, i + 1)}
                        className={`text-4xl p-3 rounded-xl transition-all ${
                          isSelected
                            ? "bg-primary/10 scale-125 ring-2 ring-primary"
                            : "hover:scale-110"
                        }`}
                        data-testid={`emoji-${i}`}
                      >
                        {emoji}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Text Question */}
              {currentQ.type === "text" && (
                <Textarea
                  placeholder="Type your response here..."
                  value={responses[currentQ.id] || ""}
                  onChange={(e) => handleResponse(currentQ.id, e.target.value)}
                  rows={4}
                  className="resize-none"
                  data-testid="text-response"
                />
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
            >
              Previous
            </Button>

            {currentQuestion < totalQuestions - 1 ? (
              <Button
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                disabled={!canProceed()}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={submitSurvey}
                disabled={!canProceed() || submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Survey
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Analytics Dashboard Component
export function SurveyAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get("/phase24/surveys/analytics");
      setAnalytics(res.data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
      toast.error("Failed to load survey analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="survey-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <MessageSquare className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{analytics?.total_responses || 0}</p>
            <p className="text-sm text-muted-foreground">Total Responses</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{analytics?.response_rate?.rate || 0}%</p>
            <p className="text-sm text-muted-foreground">Response Rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <Star className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{analytics?.average_scores?.overall || 0}%</p>
            <p className="text-sm text-muted-foreground">Overall Score</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <BarChart3 className="w-8 h-8 mx-auto text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{analytics?.nps?.score || 0}</p>
            <p className="text-sm text-muted-foreground">NPS Score</p>
          </CardContent>
        </Card>
      </div>

      {/* Score Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Score by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(analytics?.average_scores || {}).filter(([key]) => key !== "overall").map(([category, score]) => (
              <div key={category} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="capitalize">{category.replace("_", " ")}</span>
                  <span className="font-bold">{score}%</span>
                </div>
                <Progress value={score} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Staff Rankings */}
      {analytics?.by_staff?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.by_staff.slice(0, 5).map((staff, index) => (
                <div key={staff.staff_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? "bg-yellow-100 text-yellow-700" :
                      index === 1 ? "bg-gray-100 text-gray-700" :
                      index === 2 ? "bg-orange-100 text-orange-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {index + 1}
                    </div>
                    <span className="font-medium">{staff.staff_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{staff.average_score}%</p>
                    <p className="text-xs text-muted-foreground">{staff.response_count} reviews</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Feedback */}
      {analytics?.text_feedback?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.text_feedback.slice(0, 5).map((feedback, index) => (
                <div key={index} className="p-4 rounded-lg bg-slate-50 border-l-4 border-primary">
                  <p className="text-sm italic">"{feedback.text}"</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {feedback.date && new Date(feedback.date).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
